import { useEffect, useState } from 'react'

// The unread badge's state, shared by the header bell (the phone row's and
// the desktop row's: two mounts, one poll) and the inbox page (whose reads,
// archives and "mark all as read" answer the new count):
// { count, latestId, readThroughId, known, inbox, reachable }. One small
// store at module level, so the header and the page -- different trees --
// agree without a provider, and only one poll ever runs.
const INITIAL = { count: 0, latestId: null, readThroughId: null, known: false, inbox: true, reachable: false }
let state = { ...INITIAL }
const listeners = new Set()

const publish = next => {
  state = next
  listeners.forEach(fn => fn(state))
}

export const getUnread = () => state

// Merge a C-4 UnreadCount ({ count, latestId?, readThroughId? }) into the
// store; fields it leaves out keep their value.
export const setUnread = next => {
  if (!next || typeof next !== 'object') return
  const merged = { ...state, known: true }
  if (typeof next.count === 'number') merged.count = Math.max(0, next.count)
  if (next.latestId !== undefined) merged.latestId = next.latestId
  if (next.readThroughId !== undefined) merged.readThroughId = next.readThroughId
  if (state.known && merged.count === state.count && merged.latestId === state.latestId && merged.readThroughId === state.readThroughId) return
  publish(merged)
}

// The poll's own findings: whether the deployment has an inbox, and
// whether the service answered the last read.
export const setUnreadMeta = ({ inbox, reachable }) => {
  const next = { ...state }
  if (typeof inbox === 'boolean') next.inbox = inbox
  if (typeof reachable === 'boolean') next.reachable = reachable
  if (next.inbox === state.inbox && next.reachable === state.reachable) return
  publish(next)
}

// One poll however many bells: the first holder starts it (start() returns
// its stop), the last one to let go stops it and forgets the count.
let holders = 0
let stopPoll = null
export const holdUnreadPoll = start => {
  holders += 1
  if (holders === 1) stopPoll = start()
  let held = true
  return () => {
    if (!held) return
    held = false
    holders -= 1
    if (holders === 0) {
      if (stopPoll) stopPoll()
      stopPoll = null
      // The header went (signed out): the next session starts unknown.
      publish({ ...INITIAL })
    }
  }
}

// Tests, and sign-out: back to nothing known.
export const resetUnread = () => {
  if (stopPoll) stopPoll()
  stopPoll = null
  holders = 0
  publish({ ...INITIAL })
}

export const useUnread = () => {
  const [value, setValue] = useState(state)
  useEffect(() => {
    listeners.add(setValue)
    setValue(state)
    return () => listeners.delete(setValue)
  }, [])
  return value
}
